import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  Wallet,
  Building2,
  CheckCircle2,
  XCircle,
  Clock,
  User,
  Calendar,
  Euro,
  Loader2,
  AlertTriangle,
  Search,
  RefreshCw
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const WalletManagementPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pendingTopups, setPendingTopups] = useState([]);
  const [allTopups, setAllTopups] = useState([]);
  const [selectedTopup, setSelectedTopup] = useState(null);
  const [actionType, setActionType] = useState(null); // 'confirm' or 'cancel'
  const [processing, setProcessing] = useState(false);
  const [notes, setNotes] = useState('');
  const [filter, setFilter] = useState('pending'); // 'pending', 'confirmed', 'cancelled', 'all'
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchTopups();
  }, []);

  const fetchTopups = async () => {
    setLoading(true);
    try {
      const response = await api.get('/wallet/admin/pending-topups');
      setPendingTopups(response.data.pending || []);
      setAllTopups(response.data.all || response.data.pending || []);
    } catch (error) {
      console.error('Error fetching topups:', error);
      toast.error('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    if (!selectedTopup) return;
    
    setProcessing(true);
    try {
      if (actionType === 'confirm') {
        await api.post(`/wallet/admin/confirm-topup/${selectedTopup.id}`, { notes });
        toast.success('Virement confirmé - Wallet crédité');
      } else {
        await api.post(`/wallet/admin/cancel-topup/${selectedTopup.id}`, { notes });
        toast.success('Demande de virement annulée');
      }
      setSelectedTopup(null);
      setActionType(null);
      setNotes('');
      fetchTopups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'action');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" /> En attente</Badge>;
      case 'completed':
      case 'confirmed':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3 mr-1" /> Confirmé</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" /> Annulé</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredTopups = (filter === 'pending' ? pendingTopups : allTopups)
    .filter(topup => {
      if (filter !== 'all' && filter !== 'pending' && topup.status !== filter) return false;
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        return (
          topup.user_name?.toLowerCase().includes(search) ||
          topup.user_email?.toLowerCase().includes(search) ||
          topup.id?.toLowerCase().includes(search)
        );
      }
      return true;
    });

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 p-4 md:p-6 max-w-7xl mx-auto",
        collapsed ? "lg:ml-20" : "lg:ml-64"
      )} style={{ paddingTop: "130px" }}>
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
              Gestion des virements
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Validez ou annulez les demandes de rechargement par virement bancaire
            </p>
          </div>
          <Button variant="outline" onClick={fetchTopups} disabled={loading}>
            <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
            Actualiser
          </Button>
        </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-4">
            {['pending', 'confirmed', 'cancelled', 'all'].map((f) => (
              <Button
                key={f}
                variant={filter === f ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(f)}
                className={filter === f ? 'btn-primary' : ''}
              >
                {f === 'pending' && <Clock className="w-4 h-4 mr-1" />}
                {f === 'confirmed' && <CheckCircle2 className="w-4 h-4 mr-1" />}
                {f === 'cancelled' && <XCircle className="w-4 h-4 mr-1" />}
                {f === 'pending' ? 'En attente' : f === 'confirmed' ? 'Confirmés' : f === 'cancelled' ? 'Annulés' : 'Tous'}
                {f === 'pending' && pendingTopups.length > 0 && (
                  <Badge className="ml-2 bg-amber-500">{pendingTopups.length}</Badge>
                )}
              </Button>
            ))}
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher par nom, email ou référence..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : filteredTopups.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Wallet className="w-12 h-12 text-slate-300 mb-4" />
                <p className="text-slate-500">
                  {filter === 'pending' ? 'Aucune demande de virement en attente' : 'Aucune demande trouvée'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredTopups.map((topup) => (
                <Card key={topup.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      {/* Info */}
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                            <User className="w-5 h-5 text-slate-600" />
                          </div>
                          <div>
                            <p className="font-semibold">{topup.user_name || 'Client'}</p>
                            <p className="text-sm text-slate-500">{topup.user_email}</p>
                          </div>
                          {getStatusBadge(topup.status)}
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-3">
                          <div>
                            <p className="text-slate-500">Montant HT</p>
                            <p className="font-semibold">{topup.amount_ht?.toLocaleString('fr-FR')}€</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Montant TTC</p>
                            <p className="font-semibold">{topup.amount_ttc?.toLocaleString('fr-FR')}€</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Heures</p>
                            <p className="font-semibold">{topup.hours}h</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Date demande</p>
                            <p className="font-medium text-xs">
                              {new Date(topup.created_at).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                        
                        {topup.reference && (
                          <p className="text-xs text-slate-500 mt-2">
                            Réf: <span className="font-mono">{topup.reference}</span>
                          </p>
                        )}
                      </div>
                      
                      {/* Actions */}
                      {topup.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            className="border-red-200 text-red-600 hover:bg-red-50"
                            onClick={() => {
                              setSelectedTopup(topup);
                              setActionType('cancel');
                            }}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Annuler
                          </Button>
                          <Button
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => {
                              setSelectedTopup(topup);
                              setActionType('confirm');
                            }}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Confirmer
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
      </main>

      {/* Confirmation Dialog */}
      <Dialog open={!!selectedTopup && !!actionType} onOpenChange={() => { setSelectedTopup(null); setActionType(null); setNotes(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {actionType === 'confirm' ? (
                <>
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  Confirmer le virement
                </>
              ) : (
                <>
                  <XCircle className="w-5 h-5 text-red-600" />
                  Annuler la demande
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {actionType === 'confirm' 
                ? `Confirmez-vous avoir reçu le virement de ${selectedTopup?.amount_ttc?.toLocaleString('fr-FR')}€ TTC de ${selectedTopup?.user_name} ? Le wallet sera crédité de ${selectedTopup?.hours}h.`
                : `Voulez-vous annuler la demande de virement de ${selectedTopup?.user_name} ?`
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Notes (optionnel)</label>
              <Textarea
                placeholder={actionType === 'confirm' ? "Ex: Reçu le 26/01, référence bancaire..." : "Raison de l'annulation..."}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setSelectedTopup(null); setActionType(null); setNotes(''); }}>
              Annuler
            </Button>
            <Button
              onClick={handleAction}
              disabled={processing}
              className={actionType === 'confirm' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {processing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : actionType === 'confirm' ? (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              {actionType === 'confirm' ? 'Confirmer et créditer' : 'Annuler la demande'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WalletManagementPage;
